import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext, useMemo } from 'react';
import { ref, deleteObject } from 'firebase/storage';
import { where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { DonationItem, DonationCategory, DonationRequest } from '../types';
import { auth, storage } from '../firebase/config';
import { useYearContext } from './YearContext';
import { useSyncSignal } from './SheetSyncContext';
import {
    fetchCollection,
    addToCollection,
    updateInCollection,
    deleteFromCollection,
    batchUpdateFields,
    COLLECTIONS
} from '../firebase/firestoreUtils';

interface DonationsContextType {
    donationItems: DonationItem[];
    donationCategories: DonationCategory[];
    // Populated only when a Firebase user is signed in (admins). Public visitors
    // cannot read samadhi_donation_requests per firestore.rules, so this stays []
    // for them and downstream UIs should treat "empty" as "unknown, not zero".
    donationRequests: DonationRequest[];
    isLoading: boolean;
    addDonationItem: (item: Omit<DonationItem, 'id'>) => Promise<string>;
    updateDonationItem: (item: DonationItem) => Promise<void>;
    deleteDonationItem: (itemId: string) => Promise<void>;
    reorderDonationItems: (orderedIds: string[]) => Promise<void>;
}

const sortByOrder = (items: DonationItem[]): DonationItem[] =>
    [...items].sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

export const DonationsContext = createContext<DonationsContextType | undefined>(undefined);

export const DonationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedYear } = useYearContext();
    // Refetch when a Google-Sheets sync run lands (see SheetSyncContext).
    const syncSignal = useSyncSignal();
    const [donationItems, setDonationItems] = useState<DonationItem[]>([]);
    const [donationCategories, setDonationCategories] = useState<DonationCategory[]>([]);
    const [donationRequests, setDonationRequests] = useState<DonationRequest[]>([]);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!auth.currentUser);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, user => setIsAuthenticated(!!user));
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchDonations = async () => {
            setIsLoading(true);
            try {
                const [itemsData, categoriesData] = await Promise.all([
                    fetchCollection<DonationItem>(COLLECTIONS.DONATION_ITEMS, [where('year', '==', selectedYear)]),
                    fetchCollection<DonationCategory>("samadhi_donation_categories")
                ]);
                setDonationItems(sortByOrder(itemsData));
                setDonationCategories(categoriesData);
            } catch (error) {
                console.error('Failed to fetch donation data from Firestore', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDonations();
    }, [selectedYear, syncSignal]);

    // Donation requests are admin-only per firestore.rules — only fetch when a
    // user is signed in, and swallow read failures so a role without
    // donationRequests access degrades to "no counts shown" instead of blowing
    // up the whole donations UI.
    useEffect(() => {
        if (!isAuthenticated) {
            setDonationRequests([]);
            return;
        }
        let cancelled = false;
        fetchCollection<DonationRequest>(COLLECTIONS.DONATION_REQUESTS)
            .then(requests => {
                if (!cancelled) setDonationRequests(requests);
            })
            .catch(error => {
                if (!cancelled) {
                    console.warn('Failed to fetch donation requests (allocation counts will be hidden):', error);
                    setDonationRequests([]);
                }
            });
        return () => { cancelled = true; };
    }, [isAuthenticated, syncSignal]);

    const addDonationItem = useCallback(async (itemData: Omit<DonationItem, 'id'>) => {
        try {
            const dataWithYear = { ...itemData, year: (itemData as Record<string, unknown>).year || selectedYear };
            const itemId = await addToCollection<DonationItem>(COLLECTIONS.DONATION_ITEMS, dataWithYear as Omit<DonationItem, 'id'>);
            const updated = await fetchCollection<DonationItem>(COLLECTIONS.DONATION_ITEMS, [where('year', '==', selectedYear)]);
            setDonationItems(sortByOrder(updated));
            return itemId;
        } catch (error) {
            console.error("Error adding donation item: ", error);
            throw error;
        }
    }, [selectedYear]);

    const updateDonationItem = useCallback(async (updatedItemData: DonationItem) => {
        try {
            const clientOwnedFields: Partial<DonationItem> = { ...updatedItemData };
            delete clientOwnedFields.quantityAllocated;
            delete clientOwnedFields.allocationVersion;
            await updateInCollection<DonationItem>(
                COLLECTIONS.DONATION_ITEMS,
                String(updatedItemData.id),
                clientOwnedFields as DonationItem,
            );
            const updated = await fetchCollection<DonationItem>(COLLECTIONS.DONATION_ITEMS, [where('year', '==', selectedYear)]);
            setDonationItems(sortByOrder(updated));
        } catch (error) {
            console.error("Error updating donation item: ", error);
            throw error;
        }
    }, [selectedYear]);

    const deleteDonationItem = useCallback(async (itemId: string) => {
        try {
            const item = donationItems.find(i => i.id === itemId);
            if (item?.imageUrl) {
                try {
                    await deleteObject(ref(storage, `donation-items/${itemId}`));
                } catch (err) {
                    console.warn('[Storage]', 'Cleanup failed for', `donation-items/${itemId}`, ':', err instanceof Error ? err.message : String(err));
                }
            }
            await deleteFromCollection(COLLECTIONS.DONATION_ITEMS, itemId);
            const updated = await fetchCollection<DonationItem>(COLLECTIONS.DONATION_ITEMS, [where('year', '==', selectedYear)]);
            setDonationItems(sortByOrder(updated));
        } catch (error) {
            console.error("Error deleting donation item: ", error);
            throw error;
        }
    }, [donationItems, selectedYear]);

    const reorderDonationItems = useCallback(async (orderedIds: string[]) => {
        const updates = orderedIds.map((id, index) => ({ id, fields: { order: index } }));
        await batchUpdateFields(COLLECTIONS.DONATION_ITEMS, updates);
        setDonationItems(prev =>
            sortByOrder(prev.map(item => {
                const idx = orderedIds.indexOf(item.id);
                return idx >= 0 ? { ...item, order: idx } : item;
            }))
        );
    }, []);

    const value = useMemo(() => ({
        donationItems,
        donationCategories,
        donationRequests,
        isLoading,
        addDonationItem,
        updateDonationItem,
        deleteDonationItem,
        reorderDonationItems,
    }), [
        donationItems,
        donationCategories,
        donationRequests,
        isLoading,
        addDonationItem,
        updateDonationItem,
        deleteDonationItem,
        reorderDonationItems,
    ]);

    return (
        <DonationsContext.Provider value={value}>
            {children}
        </DonationsContext.Provider>
    );
};

export const useDonations = () => {
    const context = useContext(DonationsContext);
    if (!context) {
        throw new Error('useDonations must be used within a DonationsProvider');
    }
    return context;
};
